<template>
    <div class="auth-ticker">
        <div class="auth-ticker-track">
            <span v-for="(e, i) in doubled" :key="i" class="auth-ticker-item">
                <img v-if="e.icon" :src="e.icon" @error="e.icon = null" alt=""/>
                <span v-else-if="e.spriteClass" class="auth-ticker-sprite-wrap">
                    <span class="symbol" :class="e.spriteClass"></span>
                </span>
                <i v-else class="auth-ticker-mono">{{ e.mono }}</i>
                {{ e.symbol }} {{ e.price }}
                <b :class="e.up ? 'up' : 'down'">{{ e.up ? '▲' : '▼' }} {{ e.change }}%</b>
            </span>
        </div>
    </div>
</template>

<script>
import {SYMBOLS_ACTIVE} from "@/common/config"

// decorative mock quotes: plausible static prices, no live-data dependency pre-auth
const DEFAULTS = [
    {symbol: 'BTCUSD', price: '64,120.50', change: '1.24', up: true},
    {symbol: 'ETHUSD', price: '3,412.08', change: '0.86', up: true},
    {symbol: 'EURUSD', price: '1.08423', change: '0.11', up: false},
    {symbol: 'GBPUSD', price: '1.29310', change: '0.05', up: true},
    {symbol: 'XAUUSD', price: '2,391.40', change: '0.42', up: true},
    {symbol: 'US500', price: '5,522.3', change: '0.20', up: false},
    {symbol: 'AAPL', price: '228.16', change: '0.66', up: true},
    {symbol: 'TSLA', price: '246.90', change: '1.05', up: false},
    {symbol: 'USDJPY', price: '155.24', change: '0.13', up: true},
    {symbol: 'NGAS', price: '2.882', change: '2.10', up: true},
]

const CCY = ['BTC', 'ETH', 'EUR', 'GBP', 'AUD', 'CAD', 'CHF', 'JPY', 'NZD', 'XAU', 'XAG', 'USD',
    'ADA', 'SOL', 'XRP', 'LTC', 'BCH', 'DOT', 'AVAX', 'ATOM']

function iconFor(symbol) {
    const base = CCY.find(c => symbol.startsWith(c) && symbol.length > c.length)
    return base ? `/image/currencies/${base}.png` : null
}

// sprite class for non-currency symbols (64x64 sprite, displayed via zoom wrapper)
function spriteClassFor(symbol) {
    if (iconFor(symbol)) return null
    return 'symbol-' + symbol.toUpperCase().replace('.', '-')
}

export default {
    name: "AuthTicker",
    data() {
        const active = (SYMBOLS_ACTIVE || []).map(s => s.symbol)
        const bySymbol = Object.fromEntries(DEFAULTS.map(d => [d.symbol, d]))
        const entries = []
        for (const sym of active) {
            entries.push(bySymbol[sym] || {symbol: sym, price: '—', change: '0.00', up: true})
        }
        for (const d of DEFAULTS) {
            if (entries.length >= 10) break
            if (!entries.find(e => e.symbol === d.symbol)) entries.push(d)
        }
        const withIcons = entries.map(e => ({
            ...e,
            icon: iconFor(e.symbol),
            spriteClass: spriteClassFor(e.symbol),
            mono: e.symbol.slice(0, 2),
        }))
        return {doubled: [...withIcons, ...withIcons]}
    },
}
</script>

<style scoped>
.auth-ticker {
    overflow: hidden;
    border-bottom: 1px solid var(--auth-line);
    background: rgba(16, 29, 46, .55);
    font-family: 'Consolas', monospace;
    font-size: 12px;
    line-height: 34px;
    color: var(--auth-muted);
    white-space: nowrap;
}

.auth-ticker-track {
    display: inline-flex;
    animation: auth-tick 45s linear infinite;
    animation-delay: -20s; /* already mid-flow at first paint — spec requirement */
}

@keyframes auth-tick {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
}

.auth-ticker-item {
    margin-right: 42px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
}

.auth-ticker-item img { width: 17px; height: 17px; border-radius: 50%; }

/* sprite wrapper: clip to 17x17, sprite cell is 64x64 so zoom 0.265625 (= 17/64) */
.auth-ticker-sprite-wrap {
    display: inline-flex;
    width: 17px;
    height: 17px;
    overflow: hidden;
    border-radius: 50%;
    flex-shrink: 0;
}

.auth-ticker-sprite-wrap .symbol {
    zoom: 0.265625;
    flex-shrink: 0;
}

@-moz-document url-prefix() {
    .auth-ticker-sprite-wrap .symbol {
        zoom: 1;
        transform: scale(0.265625);
        transform-origin: 0 0;
    }
}

.auth-ticker-mono {
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #1b3a52;
    color: #9fc3d8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    font-weight: 700;
    font-style: normal;
}

.auth-ticker-item .up { color: #31cf5a; }
.auth-ticker-item .down { color: #dd4931; }

@media (prefers-reduced-motion: reduce) {
    .auth-ticker-track { animation: none; }
}
</style>
